import type { FC, ReactNode } from 'react'
import type { SkylineProject } from '@/pages/api/skyline'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { groupDigits } from '@/lib/format'
import { footerBus } from '@/lib/footer-bus'
import { isMotionPaused, motionStore } from '@/lib/motion'

const HEIGHT = 140
const GROUND = 104
const MAX_RISE = 70
const STEP = 3
const PULSE_EVERY = 8000
const PULSE_MS = 1800
const PULSE_BAND = 26
const MARGIN = 0.25
const GAP = 0.45

interface Profile {
    weight: number
    roof: (u: number) => number
    shade: (u: number, v: number, roof: number) => number
}

const frac = (value: number): number => value - Math.floor(value)
const near = (value: number, target: number, width: number): boolean => Math.abs(value - target) < width

/**
 * Elevation profiles for the six buildings: roof height across the width (0 to 1 of the tallest
 * rise) and the density of points at each spot, so every silhouette reads as its building.
 */
const PROFILES: Record<string, Profile> = {
    // Stepped timber roof over three storeys, ribbon windows between glulam columns
    library: {
        weight: 1.25,
        roof: u => (u < 1 / 3 ? 0.6 : u < 2 / 3 ? 0.76 : 0.92),
        shade: (u, v, roof) => {
            if (roof - v < 0.05) return 1
            if (frac(u * 8) < 0.12) return 0.85
            if ((v > 0.14 && v < 0.24) || (v > 0.42 && v < 0.52)) return 0

            return 0.5
        }
    },
    // Stacked housing slabs with vertical facade fins
    housing: {
        weight: 0.85,
        roof: u => (u < 0.58 ? 1 : 0.78),
        shade: (u, v, roof) => {
            if (roof - v < 0.04) return 1
            if (frac(v * 9) < 0.18) return 0.9
            if (frac(u * 11) < 0.16) return 0.75

            return 0.12
        }
    },
    // Tiered auditorium under a steel truss
    hall: {
        weight: 1.05,
        roof: u => 0.5 + 0.3 * (1 - Math.abs(2 * u - 1)),
        shade: (u, v, roof) => {
            if (roof - v < 0.05) return 1
            if (v > 0.47) {
                const a = frac(u * 7 + (v - 0.5) * 5)
                const b = frac(u * 7 - (v - 0.5) * 5)

                return near(a, 0.5, 0.09) || near(b, 0.5, 0.09) || near(v, 0.5, 0.025) ? 0.9 : 0
            }
            if (v < 0.36 && u > 0.18 && u < 0.82 && frac((v + u * 0.4) * 7) < 0.14) return 0.8
            if (frac(u * 9) < 0.1) return 0.6

            return 0.32
        }
    },
    // Low concrete bath house with a roof light over the pools
    bathhouse: {
        weight: 1,
        roof: u => (u > 0.4 && u < 0.6 ? 0.5 : 0.34),
        shade: (u, v, roof) => {
            if (roof - v < 0.05) return 1
            if (u > 0.4 && u < 0.6 && v > 0.34) return frac(u * 30) < 0.3 ? 0.8 : 0.15
            if (v < 0.07) return 0.9
            if (v > 0.12 && v < 0.2 && frac(u * 5) > 0.3) return 0

            return 0.62
        }
    },
    // Slender steel pavilion at a bridge head: a thin roof on posts above the deck
    pavilion: {
        weight: 0.8,
        roof: () => 0.56,
        shade: (u, v) => {
            if (v > 0.5) return 1
            if (v < 0.07) return 0.85
            if ([0.12, 0.38, 0.62, 0.88].some(post => near(u, post, 0.012))) return 0.9

            return 0
        }
    },
    // Timber gridshell over four platforms
    station: {
        weight: 1.7,
        roof: u => 0.66 * Math.pow(Math.max(0, Math.sin(Math.PI * u)), 0.55),
        shade: (u, v, roof) => {
            if (roof - v < 0.04) return 1
            if (roof - v < 0.16) {
                const a = frac(u * 16 + v * 7)
                const b = frac(u * 16 - v * 7)

                return near(a, 0.5, 0.13) || near(b, 0.5, 0.13) ? 0.85 : 0
            }
            if (v < 0.06 && frac(u * 4) > 0.12 && frac(u * 4) < 0.88) return 0.8
            if (frac(u * 4) < 0.02) return 0.6

            return 0
        }
    }
}

const FALLBACK: Array<SkylineProject> = [
    { id: '', slug: 'hallmark-library', name: 'Hallmark Library', city: 'Umeå', year: 2024, areaM2: 9400, shape: 'library' },
    { id: '', slug: 'casa-mares', name: 'Casa Mares', city: 'Lisbon', year: 2023, areaM2: 7800, shape: 'housing' },
    { id: '', slug: 'ostra-cultural-hall', name: 'Ostra Cultural Hall', city: 'Gdańsk', year: 2025, areaM2: 5200, shape: 'hall' },
    { id: '', slug: 'kelda-bath-house', name: 'Kelda Bath House', city: 'Reykjavík', year: 2022, areaM2: 2100, shape: 'bathhouse' },
    { id: '', slug: 'ribeira-bridge-pavilion', name: 'Ribeira Bridge Pavilion', city: 'Porto', year: 2021, areaM2: 640, shape: 'pavilion' },
    { id: '', slug: 'vantaa-station-roof', name: 'Vantaa Station Roof', city: 'Helsinki', year: 2026, areaM2: 12000, shape: 'station' }
]

// The footer remounts with every page, so the list is fetched once per visit.
let loaded: Array<SkylineProject> | null = null

interface Slot {
    left: number
    width: number
    room: number
}

interface Building {
    slot: Slot
    xs: Float32Array
    ys: Float32Array
    alphas: Float32Array
    top: number
}

const profileFor = (shape: string): Profile => PROFILES[shape] ?? PROFILES.library

/**
 * Splits the strip into one slot per building, weighted by how wide each building reads.
 */
const layoutSlots = (projects: Array<SkylineProject>, width: number): Array<Slot> => {
    const weights = projects.map(project => profileFor(project.shape).weight)
    const units = weights.reduce((sum, value) => sum + value, 0) + GAP * Math.max(0, projects.length - 1) + MARGIN * 2
    const unit = width / Math.max(1, units)

    let cursor = MARGIN * unit

    return weights.map((weight, index) => {
        const slot = { left: cursor, width: weight * unit, room: (weight + (index < weights.length - 1 ? GAP : MARGIN)) * unit }

        cursor += (weight + GAP) * unit

        return slot
    })
}

const buildPoints = (shape: string, slot: Slot, seed: number): Building => {
    const profile = profileFor(shape)
    const xs: Array<number> = []
    const ys: Array<number> = []
    const alphas: Array<number> = []

    let state = seed * 9301 + 49297
    let top = GROUND

    const random = (): number => {
        state = (state * 16807) % 2147483647

        return (state - 1) / 2147483646
    }

    for (let x = 0; x <= slot.width; x += STEP) {
        const u = x / Math.max(1, slot.width)
        const roof = profile.roof(u)

        for (let y = 0; y <= roof * MAX_RISE; y += STEP) {
            const v = y / MAX_RISE
            const shade = profile.shade(u, v, roof)

            if (shade <= 0 || random() > 0.35 + shade * 0.65) continue

            const px = slot.left + x + (random() - 0.5) * 1.2
            const py = GROUND - y - 1.6 + (random() - 0.5) * 1.2

            xs.push(px)
            ys.push(py)
            alphas.push(Math.min(1, shade * (0.55 + random() * 0.4)))
            top = Math.min(top, py)
        }
    }

    return { slot, xs: Float32Array.from(xs), ys: Float32Array.from(ys), alphas: Float32Array.from(alphas), top }
}

/**
 * The six Norrvik buildings standing on one ground line in points, each with a small dimension
 * line and its name. A faint scan pulse crosses every 8 seconds; hovering a building lifts it and
 * shows its year and area, and clicking opens its project page.
 */
export const FooterSkyline: FC = (): ReactNode => {
    const [projects, setProjects] = useState<Array<SkylineProject> | null>(() => loaded)
    const [slots, setSlots] = useState<Array<Slot>>([])
    const [active, setActive] = useState(-1)
    const innerRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const activeRef = useRef(-1)
    const kickRef = useRef<() => void>(() => undefined)

    useEffect(() => {
        let cancelled = false

        if (loaded) return

        fetch('/api/skyline')
            .then(response => response.json() as Promise<{ ok: boolean, projects: Array<SkylineProject> }>)
            .then(data => {
                if (data.ok && data.projects.length > 0) loaded = data.projects

                if (!cancelled) setProjects(data.projects.length > 0 ? data.projects : FALLBACK)
            })
            .catch(() => {
                if (!cancelled) setProjects(FALLBACK)
            })

        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        activeRef.current = active
        kickRef.current()
    }, [active])

    useEffect(() => {
        const inner = innerRef.current
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')

        if (!projects || !inner || !canvas || !context) return

        const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
        const still = (): boolean => reducedQuery.matches || isMotionPaused()

        let width = 0
        let buildings: Array<Building> = []
        let lifts = projects.map(() => 0)
        let pulseStart = -1
        let nextPulse = performance.now() + PULSE_EVERY
        let raf = 0
        let timer = 0
        let last = 0
        let visible = false

        const draw = (now: number): void => {
            context.clearRect(0, 0, width, HEIGHT)

            context.fillStyle = 'rgba(58, 54, 48, 1)'
            context.fillRect(0, GROUND, width, 1)

            const pulseX = pulseStart >= 0 ? -PULSE_BAND + (width + PULSE_BAND * 2) * Math.min(1, (now - pulseStart) / PULSE_MS) : -1e4

            buildings.forEach((building, index) => {
                const lift = lifts[index]
                const hovered = activeRef.current === index
                const { slot } = building

                for (let i = 0; i < building.xs.length; i++) {
                    const x = building.xs[i]
                    const distance = Math.abs(x - pulseX)
                    const glow = distance < PULSE_BAND ? 1 - distance / PULSE_BAND : 0
                    const alpha = Math.min(1, building.alphas[i] * (hovered ? 1.15 : 0.85) + glow * 0.35)

                    context.fillStyle = glow > 0.5 ? `rgba(245, 239, 227, ${alpha.toFixed(3)})` : `rgba(216, 210, 196, ${alpha.toFixed(3)})`
                    context.fillRect(x, building.ys[i] - lift, 1.5, 1.5)
                }

                // Dimension line under each building, with end ticks
                context.fillStyle = 'rgba(58, 54, 48, 1)'
                context.fillRect(slot.left, GROUND + 6, slot.width, 1)
                context.fillRect(slot.left, GROUND + 3, 1, 7)
                context.fillRect(slot.left + slot.width, GROUND + 3, 1, 7)
            })

            if (pulseStart >= 0 && pulseX > -PULSE_BAND && pulseX < width + PULSE_BAND) {
                context.fillStyle = 'rgba(134, 182, 240, 0.35)'
                context.fillRect(pulseX, GROUND - MAX_RISE - 6, 1, MAX_RISE + 6)
            }
        }

        const frame = (now: number): void => {
            raf = 0

            const k = last ? Math.min(3, (now - last) / 16.667) : 1

            last = now

            let moving = false

            lifts = lifts.map((lift, index) => {
                const goal = activeRef.current === index && !still() ? 4 : 0
                const next = lift + (goal - lift) * (1 - Math.pow(0.82, k))

                if (Math.abs(goal - next) > 0.02) moving = true

                return Math.abs(goal - next) > 0.02 ? next : goal
            })

            if (pulseStart >= 0 && now - pulseStart > PULSE_MS) pulseStart = -1

            draw(now)

            if (visible && !still() && (moving || pulseStart >= 0)) raf = requestAnimationFrame(frame)
            else last = 0
        }

        const kick = (): void => {
            if (still()) {
                lifts = lifts.map(() => 0)
                draw(performance.now())

                return
            }

            if (!raf && visible) raf = requestAnimationFrame(frame)
        }

        kickRef.current = kick

        const pulse = (): void => {
            if (still() || !visible) return

            pulseStart = performance.now()
            nextPulse = pulseStart + PULSE_EVERY
            kick()
        }

        const schedule = (): void => {
            window.clearTimeout(timer)

            if (!visible || still()) return

            timer = window.setTimeout(() => {
                if (performance.now() >= nextPulse - 50) pulse()

                schedule()
            }, Math.max(200, nextPulse - performance.now()))
        }

        const build = (): void => {
            const rect = inner.getBoundingClientRect()
            const ratio = Math.min(window.devicePixelRatio || 1, 2)

            width = rect.width
            canvas.width = Math.max(1, Math.round(width * ratio))
            canvas.height = Math.round(HEIGHT * ratio)
            context.setTransform(ratio, 0, 0, ratio, 0, 0)

            const nextSlots = layoutSlots(projects, width)

            buildings = projects.map((project, index) => buildPoints(project.shape, nextSlots[index], index + 1))
            setSlots(nextSlots)
            draw(performance.now())
        }

        const resizeObserver = new ResizeObserver(() => build())
        const intersection = new IntersectionObserver(entries => {
            visible = entries.some(entry => entry.isIntersecting)

            if (visible) {
                nextPulse = Math.max(nextPulse, performance.now() + 1200)
                schedule()
                kick()
            }
            else {
                window.clearTimeout(timer)
            }
        })

        build()
        resizeObserver.observe(inner)
        intersection.observe(inner)

        const offPulse = footerBus.on('pulse', pulse)
        const offMotion = motionStore.subscribe(() => {
            if (isMotionPaused()) {
                cancelAnimationFrame(raf)
                raf = 0
                pulseStart = -1
                lifts = lifts.map(() => 0)
                draw(performance.now())
            }
            else {
                schedule()
            }
        })

        return () => {
            cancelAnimationFrame(raf)
            window.clearTimeout(timer)
            resizeObserver.disconnect()
            intersection.disconnect()
            offPulse()
            offMotion()
            kickRef.current = () => undefined
        }
    }, [projects])

    return (
        <div className='no-scrollbar overflow-x-auto overflow-y-hidden'>
            <div ref={innerRef} className='relative min-w-[980px]' style={{ height: HEIGHT }}>
                <canvas ref={canvasRef} className='absolute inset-0 block h-full w-full' aria-hidden='true' />

                {projects && slots.length === projects.length && (
                    <ul className='absolute inset-0' aria-label='Norrvik buildings'>
                        {projects.map((project, index) => {
                            const slot = slots[index]
                            const shown = active === index
                            const tag = (column: string): string | undefined => (project.id ? `projects:${project.id}:${column}` : undefined)

                            return (
                                <li key={project.slug} className='absolute inset-y-0' style={{ left: slot.left, width: slot.width }}>
                                    <Link
                                        href={`/projects/${project.slug}`}
                                        onPointerEnter={() => setActive(index)}
                                        onPointerLeave={() => setActive(current => (current === index ? -1 : current))}
                                        onFocus={() => setActive(index)}
                                        onBlur={() => setActive(current => (current === index ? -1 : current))}
                                        className='group absolute inset-0 block'
                                        aria-label={`${project.name}, ${project.city}, ${project.year}, ${groupDigits(project.areaM2)} square metres`}
                                    >
                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'pointer-events-none absolute left-0 top-1 z-[1] flex items-center gap-2 whitespace-nowrap border border-hairline bg-panel px-2 py-1 font-mono text-[11px] uppercase leading-4 tracking-[0.1em] text-mute transition-opacity duration-300',
                                                shown ? 'opacity-100' : 'opacity-0'
                                            )}
                                        >
                                            <span className='text-signal'>
                                                {String(index + 1).padStart(2, '0')}
                                            </span>

                                            <span className='text-ink' data-mdlfy-cms={tag('name')}>
                                                {project.name}
                                            </span>

                                            <span>
                                                {project.year}
                                            </span>

                                            <span>
                                                <span>{groupDigits(project.areaM2)}</span> m²
                                            </span>
                                        </span>

                                        <span
                                            className={cn('absolute bottom-0.5 left-0 font-mono text-[11px] uppercase leading-[13px] tracking-[0.06em] transition-colors duration-300', shown ? 'text-ink' : 'text-mute')}
                                            style={{ width: slot.room - 8 }}
                                            aria-hidden='true'
                                        >
                                            <span data-mdlfy-cms={tag('name')}>
                                                {project.name}
                                            </span>
                                        </span>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
        </div>
    )
}
